/**
 * OccupationalRisk Form Component with Stepper
 * Multi-step wizard form for creating and editing Occupational Risk data
 *
 * @version 2025.1.0
 */
import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller, FormProvider, FieldPath, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  Autocomplete,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  CircularProgress,
  Alert,
  Chip,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  OccupationalRisk,
  OccupationalRiskFormData,
  OccupationalRiskStatus,
  RiskType,
  EXPOSURE_FORMS,
  EXPOSURE_TYPES,
  UNHEALTHY_DEGREES,
  OCC_UNHEALTHY_DEGREE_LABELS,
  ASSESSMENT_TYPES,
  OCC_ASSESSMENT_TYPE_LABELS,
  PENETRATION_ROUTES,
} from '../../../types/occupationalRisk';
import type { UnhealthyDegree } from '../../../types/occupationalRisk';
import {
  hazardousAgentService,
  type HazardousAgentSheet,
  type HazardousAgentSummary,
} from '../../../services/hazardousAgentService';
import { DataTable, type DataTableColumn } from '../../../components/common';
import { RiskProtectionsSection } from './RiskProtectionsSection';
import { AddProtectionDialog } from './AddProtectionDialog';
import { RiskMatrixStep } from './steps/RiskMatrixStep';
import { OCC_EXPOSURE_FORM_LABELS, OCC_EXPOSURE_TYPE_LABELS } from '@/types/occupationalRisk';
import { RISK_MATRIX_TYPES } from '../../../types/riskMatrix';
import ESocialReferenceAutocomplete from '../../companies/components/esocial/forms/ESocialReferenceAutocomplete';

// O código do agente nocivo vem da Tabela 24 do eSocial, e só dela.
const TABELA_AGENTES_NOCIVOS = 24;

// #309b — "Forma de Exposição" é texto livre; estes rótulos ficam apenas como
// sugestões do autocomplete (o usuário pode digitar qualquer valor).
const EXPOSURE_FORM_SUGGESTIONS: string[] = EXPOSURE_FORMS.map((f) => OCC_EXPOSURE_FORM_LABELS[f]);

/**
 * #417 — os campos que a ficha do agente preenche. `hazardousAgentId` fica de fora
 * porque não é conteúdo da ficha, é a ligação com ela: nunca há o que divergir.
 */
type CampoDaFicha =
  | 'inssAgentCode'
  | 'nr15Annex'
  | 'unhealthyDegree'
  | 'specialRetirementYears'
  | 'evaluationMethodology'
  | 'esocialAgentCode';

const CAMPOS_DA_FICHA: CampoDaFicha[] = [
  'inssAgentCode',
  'nr15Annex',
  'unhealthyDegree',
  'specialRetirementYears',
  'evaluationMethodology',
  'esocialAgentCode',
];

const ROTULO_DO_CAMPO: Record<CampoDaFicha, string> = {
  inssAgentCode: 'Código do agente no INSS',
  nr15Annex: 'Anexo da NR-15',
  unhealthyDegree: 'Grau de insalubridade',
  specialRetirementYears: 'Aposentadoria especial',
  evaluationMethodology: 'Metodologia de avaliação',
  esocialAgentCode: 'Código do agente nocivo (Tabela 24)',
};

const VALOR_VAZIO: Record<CampoDaFicha, string | number | null | undefined> = {
  inssAgentCode: '',
  nr15Annex: null,
  unhealthyDegree: undefined,
  specialRetirementYears: null,
  evaluationMethodology: '',
  esocialAgentCode: '',
};

const valorDaFicha = (
  ficha: HazardousAgentSheet,
  campo: CampoDaFicha,
): string | number | null => {
  switch (campo) {
    case 'inssAgentCode':
      return ficha.inssAgentCode;
    case 'nr15Annex':
      return ficha.nr15Annex;
    case 'unhealthyDegree':
      return ficha.unhealthyDegree;
    case 'specialRetirementYears':
      return ficha.specialRetirementYears;
    case 'evaluationMethodology':
      return ficha.hygieneStandards;
    case 'esocialAgentCode':
      return ficha.esocialCode;
  }
};

const textoDaFicha = (ficha: HazardousAgentSheet, campo: CampoDaFicha): string => {
  switch (campo) {
    case 'nr15Annex':
      return ficha.nr15AnnexLabel ?? `Anexo ${ficha.nr15Annex}`;
    case 'unhealthyDegree':
      return OCC_UNHEALTHY_DEGREE_LABELS[ficha.unhealthyDegree as UnhealthyDegree]
        ?? String(ficha.unhealthyDegree);
    case 'specialRetirementYears':
      return `${ficha.specialRetirementYears} anos`;
    default:
      return String(valorDaFicha(ficha, campo));
  }
};

interface ItemDaFicha {
  campo: CampoDaFicha;
  texto: string;
}

interface FichaAplicada {
  agente: string;
  preenchidos: ItemDaFicha[];
  divergentes: ItemDaFicha[];
  marcouNr15: boolean;
}

// Validation schema
const validationSchema = yup.object({
  risk: yup
    .string()
    .required('Descrição do risco é obrigatória')
    .min(10, 'Mínimo de 10 caracteres')
    .max(5000, 'Máximo de 5000 caracteres'),
  riskFactorName: yup
    .string()
    .required('Nome do fator de risco é obrigatório')
    .max(255, 'Máximo de 255 caracteres'),
  hazard: yup
    .string()
    .required('Perigo é obrigatório')
    .max(255, 'Máximo de 255 caracteres'),
  riskType: yup // ADICIONAR
    .string()
    .oneOf(Object.values(RiskType), 'Tipo de risco inválido')
    .required('Tipo de risco é obrigatório'),
  actionLevel: yup.string().max(255, 'Máximo de 255 caracteres'),
  toleranceLimit: yup.string().max(255, 'Máximo de 255 caracteres'),
  measurementUnit: yup.string().max(100, 'Máximo de 100 caracteres'),
  evaluationMethod: yup.string().max(255, 'Máximo de 255 caracteres'),
  defaultPeriodicity: yup
    .string()
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .test('is-valid-number', 'Periodicidade deve ser um número entre 1 e 360', (value) => {
      if (!value) return true;
      const num = parseInt(value, 10);
      return num >= 1 && num <= 360;
    }),
  penetrationRoute: yup.string().max(255, 'Máximo de 255 caracteres'),
  propagationMedium: yup.string().max(255, 'Máximo de 255 caracteres'),
  unhealthyAgent: yup.boolean().required(),
  hazardousAgent: yup.boolean().required(),
  socialSecurityAgent: yup.boolean().required(),
  evaluationMethodology: yup.string().max(1000, 'Máximo de 1000 caracteres'),
  legalFoundation: yup
    .string()
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .test('is-valid-format', 'Deve seguir o padrão: NR-XX, Lei XXXX, Decreto XXXX ou Portaria XXXX', (value) => {
      if (!value) return true; // Allow empty/null
      return /^(NR-\d+|Lei\s+\d+|Decreto\s+\d+|Portaria\s+\d+)/i.test(value);
    }),
  healthDamages: yup.string().max(2000, 'Máximo de 2000 caracteres'),
  determiningFactors: yup.string().nullable(),
  emergencyPreparation: yup.string().nullable(),
  exposureForm: yup.string().max(100, 'Máximo de 100 caracteres').nullable(),
  exposureType: yup.string().oneOf([...EXPOSURE_TYPES]).nullable(),
  riskMatrixType: yup.string().oneOf([...RISK_MATRIX_TYPES]).nullable(),
  riskProbability: yup.number().nullable().transform((value, original) => (original === '' ? null : value)),
  riskSeverity: yup.number().nullable().transform((value, original) => (original === '' ? null : value)),
  unhealthyDegree: yup.string().nullable(),
  assessmentType: yup.string().nullable(),
  hazardousAgentId: yup.number().nullable().transform((value, original) => (original === '' ? null : value)),
  inssAgentCode: yup.string().max(20, 'Máximo de 20 caracteres').nullable(),
  nr15Annex: yup.number().nullable().transform((value, original) => (original === '' ? null : value)),
  nr16Annex: yup.string().max(100, 'Máximo de 100 caracteres').nullable(),
  specialRetirementYears: yup.number().nullable().transform((value, original) => (original === '' ? null : value)),
  ceilingValue: yup.boolean().required(),
  skinAbsorption: yup.boolean().required(),
  esocialAgentCode: yup.string().max(20, 'Máximo de 20 caracteres'),
  esocialAgentCodeNotApplicable: yup.boolean(),
  status: yup.string().oneOf(Object.values(OccupationalRiskStatus)).required(),
});

type OccupationalRiskFormValues = yup.InferType<typeof validationSchema>;

export interface PendingProtection {
  epiId?: number | null;
  epcId?: number | null;
  epiName?: string;
  epcName?: string;
  caOrRevision?: string;
  isEffective: boolean;
  notes?: string;
}

interface OccupationalRiskFormProps {
  initialData?: Partial<OccupationalRisk>;
  onSubmit: (data: OccupationalRiskFormData & { protections?: PendingProtection[] }) => Promise<void> | void;
  onCancel: () => void;
  isSubmitting?: boolean;
  riskId?: number | string;
}

// All steps (protections always visible)
const steps = [
  'Informações Principais',
  'Classificação Regulatória',
  'Matriz de Risco',
  'Parâmetros Técnicos',
  'Exposição e Danos à Saúde',
  'Proteções (EPC/EPI)',
];

const LAST_FORM_STEP = 4; // index of last data step (submit happens here)
const PROTECTION_STEP = 5;

export const OccupationalRiskForm: React.FC<OccupationalRiskFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  riskId: initialRiskId,
}) => {
  const isEditing = Boolean(initialData?.id);
  const riskId = initialRiskId;
  const [activeStep, setActiveStep] = useState(0);
  const [pendingProtections, setPendingProtections] = useState<PendingProtection[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'EPI' | 'EPC'>('EPI');
  const formMethods = useForm({
    resolver: yupResolver(validationSchema),
    shouldUnregister: false, // Keep values when fields are unmounted (step changes)
    defaultValues: {
      risk: initialData?.risk || '',
      riskFactorName: initialData?.riskFactorName || '',
      hazard: initialData?.hazard || '',
      riskType: initialData?.riskType || RiskType.OTHER, // ADICIONAR
      actionLevel: initialData?.actionLevel || '',
      toleranceLimit: initialData?.toleranceLimit || '',
      measurementUnit: initialData?.measurementUnit || '',
      evaluationMethod: initialData?.evaluationMethod || '',
      defaultPeriodicity: initialData?.defaultPeriodicity || '',
      penetrationRoute: initialData?.penetrationRoute || '',
      propagationMedium: initialData?.propagationMedium || '',
      // Convert string codes from backend to boolean for checkboxes
      unhealthyAgent: Boolean(initialData?.unhealthyAgent),
      hazardousAgent: Boolean(initialData?.hazardousAgent),
      socialSecurityAgent: Boolean(initialData?.socialSecurityAgent),
      evaluationMethodology: initialData?.evaluationMethodology || '',
      legalFoundation: initialData?.legalFoundation || '',
      healthDamages: initialData?.healthDamages || '',
      determiningFactors: initialData?.determiningFactors || '',
      emergencyPreparation: initialData?.emergencyPreparation || '',
      exposureForm: initialData?.exposureForm ?? null,
      exposureType: initialData?.exposureType ?? null,
      riskMatrixType: initialData?.riskMatrixType ?? null,
      riskProbability: initialData?.riskProbability ?? null,
      riskSeverity: initialData?.riskSeverity ?? null,
      unhealthyDegree: initialData?.unhealthyDegree ?? undefined,
      assessmentType: initialData?.assessmentType ?? undefined,
      hazardousAgentId: initialData?.hazardousAgentId ?? null,
      inssAgentCode: initialData?.inssAgentCode ?? '',
      nr15Annex: initialData?.nr15Annex ?? null,
      nr16Annex: initialData?.nr16Annex ?? '',
      specialRetirementYears: initialData?.specialRetirementYears ?? null,
      ceilingValue: Boolean(initialData?.ceilingValue),
      skinAbsorption: Boolean(initialData?.skinAbsorption),
      esocialAgentCode: initialData?.esocialAgentCode || '',
      esocialAgentCodeNotApplicable: Boolean(initialData?.esocialAgentCodeNotApplicable),
      status: initialData?.status || OccupationalRiskStatus.ACTIVE,
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
    setValue,
  } = formMethods;

  // #306 — o grau de insalubridade só aparece quando NR-15 está marcado
  const unhealthyAgentChecked = useWatch({ control, name: 'unhealthyAgent' });

  // #473 — a marcação "não é agente nocivo do eSocial" viaja de ida e volta mesmo sem
  // controle próprio na ficha: sem ela no formulário, salvar o risco pela tela apagaria em
  // silêncio o que o painel em lote gravou. Aqui ela também explica o campo do código.
  const codigoNaoSeAplica = useWatch({ control, name: 'esocialAgentCodeNotApplicable' });

  // #417 — seletor próprio sobre os 400 agentes do catálogo. O da Tabela 24, na
  // classificação, continua servindo ao código do eSocial: ele alcança só 92 agentes.
  const [agentesDoCatalogo, setAgentesDoCatalogo] = useState<HazardousAgentSummary[]>([]);
  const [agenteEscolhido, setAgenteEscolhido] = useState<HazardousAgentSummary | null>(null);
  const [buscandoNoCatalogo, setBuscandoNoCatalogo] = useState(false);
  const [catalogoIndisponivel, setCatalogoIndisponivel] = useState(false);
  const [fichaAplicada, setFichaAplicada] = useState<FichaAplicada | null>(null);
  const camposVindosDaFicha = useRef<Set<CampoDaFicha>>(new Set());
  const buscaEmEspera = useRef<ReturnType<typeof setTimeout>>(undefined);

  /**
   * Três regras (D7 da spec): campo vazio é preenchido; campo digitado à mão sobrevive
   * e fica assinalado; campo que veio do catálogo é substituído quando o agente muda —
   * senão quem escolheu "Ruído" e trocou para "Benzeno" fica com os dados do ruído.
   */
  const aplicarFicha = (ficha: HazardousAgentSheet) => {
    camposVindosDaFicha.current.forEach((campo) => setValue(campo, VALOR_VAZIO[campo] as never));
    camposVindosDaFicha.current.clear();

    const preenchidos: ItemDaFicha[] = [];
    const divergentes: ItemDaFicha[] = [];

    CAMPOS_DA_FICHA.forEach((campo) => {
      const valor = valorDaFicha(ficha, campo);
      if (valor === null || valor === undefined || valor === '') return;

      const item = { campo, texto: textoDaFicha(ficha, campo) };
      const atual = getValues(campo);

      if (atual === null || atual === undefined || atual === '') {
        setValue(campo, valor as never, { shouldDirty: true });
        camposVindosDaFicha.current.add(campo);
        preenchidos.push(item);
        return;
      }

      divergentes.push(item);
    });

    // Código e "não se aplica" são exclusivos: o código que a ficha trouxe tira a marcação.
    if (camposVindosDaFicha.current.has('esocialAgentCode')) {
      setValue('esocialAgentCodeNotApplicable', false, { shouldDirty: true });
    }

    setValue('hazardousAgentId', ficha.id, { shouldDirty: true });

    // O grau só é renderizado com a caixa NR-15 marcada, e ela fica no passo seguinte:
    // sem marcar aqui, um adicional de 40% entraria escondido.
    const veioGrau = preenchidos.some((item) => item.campo === 'unhealthyDegree');
    const marcouNr15 = veioGrau && !getValues('unhealthyAgent');
    if (veioGrau) setValue('unhealthyAgent', true, { shouldDirty: true });

    setFichaAplicada({ agente: ficha.displayName, preenchidos, divergentes, marcouNr15 });
  };

  const escolherAgenteDoCatalogo = async (agente: HazardousAgentSummary | null) => {
    setAgenteEscolhido(agente);

    if (!agente) {
      setFichaAplicada(null);
      setValue('hazardousAgentId', null, { shouldDirty: true });
      return;
    }

    try {
      aplicarFicha(await hazardousAgentService.getSheet(agente.id));
      setCatalogoIndisponivel(false);
    } catch {
      setCatalogoIndisponivel(true);
    }
  };

  const buscarNoCatalogo = (termo: string) => {
    clearTimeout(buscaEmEspera.current);

    if (termo.trim().length < 1) {
      setAgentesDoCatalogo([]);
      return;
    }

    buscaEmEspera.current = setTimeout(async () => {
      setBuscandoNoCatalogo(true);
      try {
        setAgentesDoCatalogo(await hazardousAgentService.search(termo.trim()));
        setCatalogoIndisponivel(false);
      } catch {
        // Catálogo fora do ar não impede cadastro: os campos ficam como estão.
        setAgentesDoCatalogo([]);
        setCatalogoIndisponivel(true);
      } finally {
        setBuscandoNoCatalogo(false);
      }
    }, 300);
  };

  // No risco já ligado a um agente, o que bate com a ficha veio dela — e por isso pode
  // ser substituído quando o técnico trocar de agente.
  useEffect(() => {
    const idDoAgente = initialData?.hazardousAgentId;
    if (!idDoAgente) return;

    hazardousAgentService
      .getSheet(idDoAgente)
      .then((ficha) => {
        setAgenteEscolhido(ficha);
        CAMPOS_DA_FICHA.forEach((campo) => {
          if (valorDaFicha(ficha, campo) === getValues(campo)) camposVindosDaFicha.current.add(campo);
        });
      })
      .catch(() => setCatalogoIndisponivel(true));
  }, [initialData?.hazardousAgentId, getValues]);

  // Step navigation handlers with validation
  const handleNext = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    console.log('🔍 [Form] === NEXT BUTTON CLICKED ===');
    console.log('🔍 [Form] Event:', e);
    console.log('🔍 [Form] Current step:', activeStep);
    console.log('🔍 [Form] Total steps:', steps.length);
    console.log('🔍 [Form] Current form values:', getValues());

    // Prevent default form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      console.log('🔍 [Form] Prevented default event');
    }

    const fieldsToValidate = getFieldsForStep(activeStep);
    console.log('🔍 [Form] Fields to validate:', fieldsToValidate);

    const isValid = await trigger(fieldsToValidate);
    console.log('🔍 [Form] Validation result:', isValid);

    if (!isValid) {
      console.error('❌ [Form] Validation failed, errors:', errors);
      console.error('❌ [Form] Current values for failed fields:',
        fieldsToValidate.reduce((acc, field) => {
          acc[field] = getValues(field);
          return acc;
        }, {} as Record<string, unknown>)
      );
    } else {
      console.log('✅ [Form] Validation passed, moving to step', activeStep + 1);
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    console.log('🔙 [Form] Going back from step', activeStep, 'to step', activeStep - 1);
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Wrap onSubmit - include pending protections for create
  const handleFormSubmit = async (data: OccupationalRiskFormValues) => {
    try {
      const payload: OccupationalRiskFormData & { protections?: PendingProtection[] } = data as OccupationalRiskFormData;
      // For create: attach pending protections to the payload
      if (!isEditing && pendingProtections.length > 0) {
        payload.protections = pendingProtections.map(p => ({
          epiId: p.epiId,
          epcId: p.epcId,
          isEffective: p.isEffective,
          notes: p.notes,
        }));
      }
      await onSubmit(payload);
    } catch (error) {
      console.error('❌ [Form] Error in onSubmit:', error);
    }
  };

  // Get fields to validate for each step
  const getFieldsForStep = (step: number): FieldPath<OccupationalRiskFormValues>[] => {
    switch (step) {
      case 0:
        return ['riskFactorName', 'hazard', 'risk', 'riskType', 'status'];
      case 1:
        return ['unhealthyAgent', 'hazardousAgent', 'socialSecurityAgent', 'legalFoundation'];
      case 2:
        return []; // Matriz de Risco — all optional
      case 3:
        return ['actionLevel', 'toleranceLimit', 'measurementUnit', 'evaluationMethod', 'defaultPeriodicity'];
      case 4:
        return ['penetrationRoute', 'propagationMedium', 'evaluationMethodology', 'healthDamages', 'determiningFactors', 'emergencyPreparation', 'exposureForm', 'exposureType'];
      default:
        return [];
    }
  };

  // Step 1: Main Information
  const renderMainInformation = () => (
    <Grid container spacing={3}>
      {/* #417 — agente do catálogo: escolher preenche a ficha do risco */}
      <Grid size={12}>
        <Autocomplete
          value={agenteEscolhido}
          onChange={(_, agente) => { void escolherAgenteDoCatalogo(agente); }}
          options={agentesDoCatalogo}
          loading={buscandoNoCatalogo}
          disabled={isSubmitting}
          fullWidth
          filterOptions={(opcoes) => opcoes}
          getOptionLabel={(agente) => agente.displayName}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onInputChange={(_, termo, motivo) => { if (motivo === 'input') buscarNoCatalogo(termo); }}
          noOptionsText="Digite para buscar no catálogo"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Agente do Catálogo (NR-15, NR-16 e INSS)"
              helperText="Busque pelo nome do agente, pelo número CAS ou pelo código do eSocial. Preenche anexo, grau, código do INSS e tempo de aposentadoria especial."
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {buscandoNoCatalogo && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />

        {catalogoIndisponivel && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Não foi possível consultar o catálogo de agentes agora. Os campos ficam como estão e o
            risco pode ser salvo normalmente.
          </Alert>
        )}

        {fichaAplicada && (
          <Alert severity="info" icon={false} sx={{ mt: 1 }}>
            <Typography variant="subtitle2">Ficha de {fichaAplicada.agente}</Typography>
            {fichaAplicada.preenchidos.length === 0 ? (
              <Typography variant="body2">
                A ficha não traz anexo, grau nem código do INSS para este agente.
              </Typography>
            ) : (
              fichaAplicada.preenchidos.map((item) => (
                <Typography key={item.campo} variant="body2">
                  {ROTULO_DO_CAMPO[item.campo]}: {item.texto}
                </Typography>
              ))
            )}
            {fichaAplicada.marcouNr15 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                A caixa &quot;Agente Insalubre (NR-15)&quot; foi marcada por causa do grau.
              </Typography>
            )}
            {fichaAplicada.divergentes.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {`Mantido o que você digitou, divergente da ficha: ${fichaAplicada.divergentes
                  .map((item) => `${ROTULO_DO_CAMPO[item.campo]} (ficha: ${item.texto})`)
                  .join(' · ')}`}
              </Typography>
            )}
          </Alert>
        )}
      </Grid>

      {/* Risk Factor Name */}
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Controller
          name="riskFactorName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Nome do Fator de Risco"
              fullWidth
              required
              error={Boolean(errors.riskFactorName)}
              helperText={errors.riskFactorName?.message}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      {/* Hazard (Perigo) */}
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Controller
          name="hazard"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Perigo ou Fator de Risco"
              fullWidth
              required
              error={Boolean(errors.hazard)}
              helperText={errors.hazard?.message || 'Ex: Ruído, Benzeno, Bactérias, etc'}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      {/* NOVO: Risk Type */}
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <FormControl fullWidth required error={Boolean(errors.riskType)}>
          <InputLabel>Tipo de Risco</InputLabel>
          <Controller
            name="riskType"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                label="Tipo de Risco"
                disabled={isSubmitting}
              >
                <MenuItem value={RiskType.PHYSICAL}>Físico</MenuItem>
                <MenuItem value={RiskType.CHEMICAL}>Químico</MenuItem>
                <MenuItem value={RiskType.BIOLOGICAL}>Biológico</MenuItem>
                <MenuItem value={RiskType.ERGONOMIC}>Ergonômico</MenuItem>
                <MenuItem value={RiskType.ACCIDENT}>Acidente</MenuItem>
                <MenuItem value={RiskType.OTHER}>Outro</MenuItem>
              </Select>
            )}
          />
          {errors.riskType && <FormHelperText>{errors.riskType.message}</FormHelperText>}
        </FormControl>
      </Grid>

      {/* Status */}
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <FormControl fullWidth required>
          <InputLabel>Status</InputLabel>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select {...field} label="Status" disabled={isSubmitting}>
                <MenuItem value={OccupationalRiskStatus.ACTIVE}>Ativo</MenuItem>
                <MenuItem value={OccupationalRiskStatus.INACTIVE}>Inativo</MenuItem>
              </Select>
            )}
          />
        </FormControl>
      </Grid>

      {/* Risk Description */}
      <Grid size={12}>
        <Controller
          name="risk"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Descrição Detalhada do Risco"
              fullWidth
              required
              multiline
              rows={3}
              error={Boolean(errors.risk)}
              helperText={errors.risk?.message || 'Mínimo 10 caracteres'}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>
    </Grid>
  );

  // Step 2: Regulatory Classification
  const renderRegulatoryClassification = () => (
    <Grid container spacing={3}>
      <Grid
        size={{
          xs: 12,
          md: 4
        }}>
        <Controller
          name="unhealthyAgent"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={field.value} disabled={isSubmitting} />}
              label="Agente Insalubre (NR-15)"
            />
          )}
        />
      </Grid>

      <Grid
        size={{
          xs: 12,
          md: 4
        }}>
        <Controller
          name="hazardousAgent"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={field.value} disabled={isSubmitting} />}
              label="Agente Perigoso (NR-16)"
            />
          )}
        />
      </Grid>

      <Grid
        size={{
          xs: 12,
          md: 4
        }}>
        <Controller
          name="socialSecurityAgent"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={field.value} disabled={isSubmitting} />}
              label="Agente Previdenciário"
            />
          )}
        />
      </Grid>

      {/* #306 — Grau de insalubridade (só quando NR-15 marcado), em linha própria */}
      {unhealthyAgentChecked && (
        <Grid size={12}>
          <Controller
            name="unhealthyDegree"
            control={control}
            render={({ field }) => (
              <FormControl
                error={Boolean(errors.unhealthyDegree)}
                disabled={isSubmitting}
                sx={{ width: '100%', maxWidth: 360 }}
              >
                <InputLabel id="grau-insalubridade-label">Grau de Insalubridade</InputLabel>
                <Select
                  {...field}
                  labelId="grau-insalubridade-label"
                  value={field.value ?? ''}
                  label="Grau de Insalubridade"
                  onChange={(e) => field.onChange(e.target.value || undefined)}
                >
                  <MenuItem value=""><em>Não informado</em></MenuItem>
                  {UNHEALTHY_DEGREES.map((d) => (
                    <MenuItem key={d} value={d}>{OCC_UNHEALTHY_DEGREE_LABELS[d]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
      )}

      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Controller
          name="legalFoundation"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Fundamentação Legal"
              fullWidth
              error={Boolean(errors.legalFoundation)}
              helperText={errors.legalFoundation?.message || 'Ex: NR-15, NR-16, Lei 8.213/91'}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Controller
          name="esocialAgentCode"
          control={control}
          render={({ field }) => (
            <ESocialReferenceAutocomplete
              tableNumber={TABELA_AGENTES_NOCIVOS}
              value={field.value || ''}
              onChange={(codigo) => {
                field.onChange(codigo);
                if (codigo) setValue('esocialAgentCodeNotApplicable', false, { shouldDirty: true });
              }}
              label="Código do agente nocivo (Tabela 24)"
              helperText={
                codigoNaoSeAplica
                  ? 'Este risco está marcado como "não se aplica" no eSocial. Escolher um código remove a marcação.'
                  : 'Busque o agente nocivo pelo nome ou pelo código da Tabela 24 do eSocial'
              }
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      {/* #417 — o que a ficha do agente preenche, e que continua editável à mão */}
      <Grid size={{ xs: 12, md: 3 }}>
        <Controller
          name="inssAgentCode"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Código do Agente no INSS"
              fullWidth
              error={Boolean(errors.inssAgentCode)}
              helperText={errors.inssAgentCode?.message || 'Anexo IV do Decreto 3.048/99'}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 3 }}>
        <Controller
          name="nr15Annex"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
              label="Anexo da NR-15"
              type="number"
              fullWidth
              error={Boolean(errors.nr15Annex)}
              helperText={errors.nr15Annex?.message}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 3 }}>
        <Controller
          name="nr16Annex"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Anexo da NR-16"
              fullWidth
              error={Boolean(errors.nr16Annex)}
              helperText={errors.nr16Annex?.message}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 3 }}>
        <Controller
          name="specialRetirementYears"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
              label="Aposentadoria Especial (anos)"
              type="number"
              fullWidth
              error={Boolean(errors.specialRetirementYears)}
              helperText={errors.specialRetirementYears?.message}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>
    </Grid>
  );

  // Step 3: Technical Parameters
  const renderTechnicalParameters = () => (
    <Grid container spacing={3}>
      <Grid
        size={{
          xs: 12,
          md: 4
        }}>
        <Controller
          name="actionLevel"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Nível de Ação"
              fullWidth
              error={Boolean(errors.actionLevel)}
              helperText={errors.actionLevel?.message}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      <Grid
        size={{
          xs: 12,
          md: 4
        }}>
        <Controller
          name="toleranceLimit"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Limite de Tolerância"
              fullWidth
              error={Boolean(errors.toleranceLimit)}
              helperText={errors.toleranceLimit?.message}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      <Grid
        size={{
          xs: 12,
          md: 4
        }}>
        <Controller
          name="measurementUnit"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Unidade de Medida"
              fullWidth
              error={Boolean(errors.measurementUnit)}
              helperText={errors.measurementUnit?.message || 'Ex: dB(A), ppm, mg/m³'}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Controller
          name="evaluationMethod"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Método de Avaliação"
              fullWidth
              error={Boolean(errors.evaluationMethod)}
              helperText={errors.evaluationMethod?.message}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Controller
          name="defaultPeriodicity"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Periodicidade Padrão (meses)"
              fullWidth
              type="number"
              error={Boolean(errors.defaultPeriodicity)}
              helperText={errors.defaultPeriodicity?.message || 'Periodicidade de avaliação em meses (NR-07)'}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      {/* #308 — Natureza da avaliação */}
      <Grid size={{ xs: 12, md: 4 }}>
        <Controller
          name="assessmentType"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={Boolean(errors.assessmentType)} disabled={isSubmitting}>
              <InputLabel>Avaliação</InputLabel>
              <Select
                {...field}
                value={field.value ?? ''}
                label="Avaliação"
                onChange={(e) => field.onChange(e.target.value || undefined)}
              >
                <MenuItem value=""><em>Não informado</em></MenuItem>
                {ASSESSMENT_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{OCC_ASSESSMENT_TYPE_LABELS[t]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>

      {/* #308 — Valor Teto e Absorção pela Pele (flags independentes) */}
      <Grid size={{ xs: 12, md: 8 }}>
        <Controller
          name="ceilingValue"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={field.value} disabled={isSubmitting} />}
              label="Valor Teto"
            />
          )}
        />
        <Controller
          name="skinAbsorption"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox {...field} checked={field.value} disabled={isSubmitting} />}
              label="Absorção pela Pele"
            />
          )}
        />
      </Grid>
    </Grid>
  );

  // Step 4: Exposure and Health Damages
  const renderExposureAndDamages = () => (
    <Grid container spacing={3}>
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Controller
          name="penetrationRoute"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={Boolean(errors.penetrationRoute)} disabled={isSubmitting}>
              <InputLabel>Via de Penetração</InputLabel>
              <Select
                {...field}
                value={field.value ?? ''}
                label="Via de Penetração"
                onChange={(e) => field.onChange(e.target.value || undefined)}
              >
                <MenuItem value=""><em>Não informada</em></MenuItem>
                {PENETRATION_ROUTES.map((route) => (
                  <MenuItem key={route} value={route}>{route}</MenuItem>
                ))}
                {field.value && !(PENETRATION_ROUTES as readonly string[]).includes(field.value) && (
                  <MenuItem value={field.value}>{field.value}</MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        />
      </Grid>

      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Controller
          name="propagationMedium"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Meio de Propagação"
              fullWidth
              error={Boolean(errors.propagationMedium)}
              helperText={errors.propagationMedium?.message || 'Ex: Ar, Água, Contato direto'}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      {/* Forma de Exposição — #309b: texto livre com sugestões (há muitas formas possíveis) */}
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Controller
          name="exposureForm"
          control={control}
          render={({ field }) => (
            <Autocomplete
              freeSolo
              options={EXPOSURE_FORM_SUGGESTIONS}
              inputValue={field.value ?? ''}
              onInputChange={(_, newInputValue) => field.onChange(newInputValue)}
              disabled={isSubmitting}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Forma de Exposição"
                  fullWidth
                  error={Boolean(errors.exposureForm)}
                  helperText={errors.exposureForm?.message}
                />
              )}
            />
          )}
        />
      </Grid>

      {/* Tipo de Exposição */}
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <FormControl fullWidth error={Boolean(errors.exposureType)}>
          <InputLabel>Tipo de Exposição</InputLabel>
          <Controller
            name="exposureType"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                value={field.value ?? ''}
                label="Tipo de Exposição"
                disabled={isSubmitting}
              >
                <MenuItem value=""><em>Nenhum</em></MenuItem>
                {EXPOSURE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {OCC_EXPOSURE_TYPE_LABELS[type]}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors.exposureType && <FormHelperText>{errors.exposureType.message}</FormHelperText>}
        </FormControl>
      </Grid>

      <Grid size={12}>
        <Controller
          name="evaluationMethodology"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Metodologia de Avaliação"
              fullWidth
              multiline
              rows={3}
              error={Boolean(errors.evaluationMethodology)}
              helperText={errors.evaluationMethodology?.message}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      <Grid size={12}>
        <Controller
          name="healthDamages"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Danos à Saúde"
              fullWidth
              multiline
              rows={3}
              error={Boolean(errors.healthDamages)}
              helperText={errors.healthDamages?.message || 'Descreva os possíveis danos à saúde'}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      {/* Fatores Determinantes */}
      <Grid size={12}>
        <Controller
          name="determiningFactors"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Fatores Determinantes"
              fullWidth
              multiline
              rows={3}
              error={Boolean(errors.determiningFactors)}
              helperText={errors.determiningFactors?.message || 'Descreva os fatores que determinam a exposição ao perigo.'}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>

      {/* Preparação para Emergências */}
      <Grid size={12}>
        <Controller
          name="emergencyPreparation"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Preparação para Emergências"
              fullWidth
              multiline
              rows={3}
              error={Boolean(errors.emergencyPreparation)}
              helperText={errors.emergencyPreparation?.message || 'Descreva os procedimentos e preparações para situações de emergência.'}
              disabled={isSubmitting}
            />
          )}
        />
      </Grid>
    </Grid>
  );

  const pendingProtectionColumns: DataTableColumn<PendingProtection>[] = [
    {
      id: 'type',
      label: 'Tipo',
      render: (p) => <Chip label={p.epiId ? 'EPI' : 'EPC'} size="small" variant="outlined" />,
    },
    {
      id: 'name',
      label: 'Nome',
      primary: true,
      render: (p) => p.epiName || p.epcName,
    },
    {
      id: 'effective',
      label: 'Eficaz',
      render: (p) => (p.isEffective ? 'Sim' : 'Não'),
    },
    {
      id: 'notes',
      label: 'Observações',
      render: (p) => p.notes || '-',
    },
  ];

  return (
    <FormProvider {...formMethods}>
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      {/* Stepper with background */}
      <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Step Content */}
      <Box>
        <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
          {renderMainInformation()}
        </Box>
        <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
          {renderRegulatoryClassification()}
        </Box>
        <Box sx={{ display: activeStep === 2 ? 'block' : 'none' }}>
          <RiskMatrixStep />
        </Box>
        <Box sx={{ display: activeStep === 3 ? 'block' : 'none' }}>
          {renderTechnicalParameters()}
        </Box>
        <Box sx={{ display: activeStep === 4 ? 'block' : 'none' }}>
          {renderExposureAndDamages()}
        </Box>
        <Box sx={{ display: activeStep === PROTECTION_STEP ? 'block' : 'none' }}>
          {/* For edit with existing risk: show API-based section */}
          {isEditing && riskId ? (
            <RiskProtectionsSection riskId={riskId} />
          ) : (
            /* For create: show local pending protections */
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Proteções (EPC/EPI)
                  {pendingProtections.length > 0 && (
                    <Chip label={pendingProtections.length} size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />
                  )}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => { setDialogType('EPI'); setDialogOpen(true); }}>
                    + Vincular EPI
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => { setDialogType('EPC'); setDialogOpen(true); }}>
                    + Vincular EPC
                  </Button>
                </Box>
              </Box>
              {pendingProtections.length === 0 ? (
                <Alert severity="info">Nenhuma proteção adicionada ainda.</Alert>
              ) : (
                <DataTable
                  columns={pendingProtectionColumns}
                  rows={pendingProtections}
                  getRowId={(p) => pendingProtections.indexOf(p)}
                  emptyState={{
                    title: 'Nenhuma proteção adicionada ainda',
                    description:
                      'Use os botões "Vincular EPI" e "Vincular EPC" acima para adicionar as proteções deste risco.',
                  }}
                  actions={[
                    {
                      icon: <DeleteIcon fontSize="small" />,
                      tooltip: 'Remover',
                      color: 'error',
                      onClick: (p) => setPendingProtections(prev => prev.filter((item) => item !== p)),
                    },
                  ]}
                />
              )}
              <AddProtectionDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmit={async (data) => {
                  // The dialog passes epiName/epcName/caOrRevision beyond the declared type
                  const extended = data as typeof data & { epiName?: string; epcName?: string; caOrRevision?: string };
                  // Add to local state, not API
                  setPendingProtections(prev => [...prev, {
                    epiId: extended.epiId,
                    epcId: extended.epcId,
                    epiName: extended.epiName,
                    epcName: extended.epcName,
                    caOrRevision: extended.caOrRevision,
                    isEffective: extended.isEffective ?? true,
                    notes: extended.notes,
                  }]);
                }}
                protectionType={dialogType}
              />
            </>
          )}
        </Box>
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          type="button"
          onClick={onCancel}
          variant="outlined"
          startIcon={<CancelIcon />}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeStep > 0 && (
            <Button
              type="button"
              onClick={handleBack}
              variant="outlined"
              startIcon={<BackIcon />}
              disabled={isSubmitting}
            >
              Voltar
            </Button>
          )}

          {activeStep < LAST_FORM_STEP ? (
            <Button
              type="button"
              onClick={handleNext}
              variant="contained"
              endIcon={<NextIcon />}
              disabled={isSubmitting}
            >
              Próximo
            </Button>
          ) : activeStep === LAST_FORM_STEP ? (
            <Button
              type="button"
              onClick={handleNext}
              variant="contained"
              endIcon={<NextIcon />}
              disabled={isSubmitting}
            >
              Próximo
            </Button>
          ) : (
            /* Step 5 - submit everything */
            <Button
              type="submit"
              variant="contained"
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={isSubmitting}
            >
              {isEditing ? 'Atualizar' : 'Criar'} Risco
            </Button>
          )}
        </Box>
      </Box>
    </Box>
    </FormProvider>
  );
};

export default OccupationalRiskForm;
